import { useCallback } from "react";
import type { Offer, OfferStatus } from "@/hooks/use-offers-queries";
import { useUpdateOffer } from "@/hooks/use-offer-mutations";
import { buildStatusPatch } from "@/lib/offer-status-patch";
import type { OfferProbability } from "@/lib/offer-pipeline-calc";

export type { Offer, OfferStatus };

export type OfferForStatusChange = Pick<
  Offer,
  | "id"
  | "status"
  | "sent_at"
  | "accepted_at"
  | "valid_until"
  // VIB-2094: buildStatusPatch trenger dem for å avgjøre om tilbudet har det
  // som kreves for å sendes.
  | "probability"
  | "expected_close_date"
>;

/**
 * Delt statusendrings-hook for tilbud (VIB-1304). Brukes av BÅDE
 * tilbudseditoren (via useOfferPageHandlers) og kanban-boardet
 * (OfferPipelineBoard) — SAMME kodesti, jf. spec "Statusflyt": sent_at/
 * accepted_at/lost_at/valid_until settes identisk fra begge flater.
 *
 * `pipelineEnabled` styres av kalleren (leser feature_offer_pipeline via
 * useFeatureFlag) — når false brukes pre-VIB-1304-semantikk (kun sent_at/
 * accepted_at, aldri valid_until/lost_at/lost_reason), slik at hele pakken
 * forblir mørk til Erik ruller ut flagget.
 */
export function useOfferStatusChange(pipelineEnabled: boolean) {
  const updateOffer = useUpdateOffer();

  const changeStatus = useCallback(
    (
      offer: OfferForStatusChange,
      newStatus: OfferStatus,
      opts: {
        lostReason?: string;
        /** VIB-2094: samlet inn av SendOfferDialog før sending. */
        sendFields?: { probability?: OfferProbability; expectedCloseDate?: string };
      } = {},
    ) => {
      const patch = buildStatusPatch(offer, newStatus, {
        lostReason: opts.lostReason,
        sendFields: opts.sendFields,
        pipelineFields: pipelineEnabled,
      });
      updateOffer.mutate({ id: offer.id, patch });
    },
    [updateOffer, pipelineEnabled],
  );

  return { changeStatus, isPending: updateOffer.isPending };
}
